import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, CheckCircleIcon, SendIcon, BuildingIcon, UsersIcon, BeakerIcon, NewspaperIcon, ShieldCheckIcon } from 'lucide-react';
export const ContactPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const departments = [{
    name: 'General Inquiries',
    email: 'info@ukweliinstitute.org',
    icon: <BuildingIcon size={20} className="text-blue-600" data-id="element-592" data-mp="sa9nbf3c4bwz" />
  }, {
    name: 'Research Collaboration',
    email: 'research@ukweliinstitute.org',
    icon: <BeakerIcon size={20} className="text-blue-600" data-id="element-593" data-mp="11d11hmnlhlsb" />
  }, {
    name: 'Certification Program',
    email: 'certification@ukweliinstitute.org',
    icon: <ShieldCheckIcon size={20} className="text-blue-600" data-id="element-594" data-mp="x8tepv0p6fwq" />
  }, {
    name: 'Media & Press',
    email: 'media@ukweliinstitute.org',
    icon: <NewspaperIcon size={20} className="text-blue-600" data-id="element-595" data-mp="4nbb4tm8zq4v" />
  }, {
    name: 'Volunteer Opportunities',
    email: 'volunteer@ukweliinstitute.org',
    icon: <UsersIcon size={20} className="text-blue-600" data-id="element-596" data-mp="dmyehkyjf60k" />
  }];
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const validateForm = () => {
    let valid = true;
    const errors = {
      name: '',
      email: '',
      message: ''
    };
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      valid = false;
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      valid = false;
    }
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
      valid = false;
    }
    setFormErrors(errors);
    return valid;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'General Inquiry',
        message: ''
      });
    }, 1500);
  };
  return <div className="bg-white w-full" data-id="element-597" data-mp="2sdkl87sdwvf2">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-598" data-mp="2sfx1mgihuxq0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-599" data-mp="gpfhrzt3tzsc">
          <div className="text-center" data-id="element-600" data-mp="br46rlr2pj8j">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-601" data-mp="bs5vuu4xc699">
              Contact Us
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-602" data-mp="6yksemkkak2o">
              Get in touch with our team to learn more about our research,
              certification programs, or partnership opportunities
            </p>
          </div>
        </div>
      </section>
      {/* Contact Form Section */}
      <section className="py-16 bg-gray-50" data-id="element-603" data-mp="3k7ft9oxvcnap">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-604" data-mp="3k8ikxz30mtro">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-605" data-mp="3f8me7s2xoo8f">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="lg:col-span-1" data-id="element-606" data-mp="3fah1ve5laug7">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-607" data-mp="15e4jficbpca">
                Get In Touch
              </h2>
              <p className="text-gray-700 mb-8" data-id="element-608" data-mp="63z55g1i8b19">
                Have questions about our research, certification programs, or
                how we can collaborate? Fill out the form and our team will get
                back to you as soon as possible.
              </p>
              <div className="mb-8" data-id="element-609" data-mp="3sdjmmevi3y2f">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-610" data-mp="3el3eiznqt9un">
                  Contact Departments
                </h3>
                <div className="space-y-4" data-id="element-611" data-mp="3jjwv50ybtgz1">
                  {departments.map((dept, index) => <div key={dept.name} className="flex items-start" data-id="element-612" data-mp="34mwatiigsoal" data-mpi={index}>
                      <div className="mr-3 mt-1" data-id="element-613" data-mp="39k5v6jqzy9rk">{dept.icon}</div>
                      <div data-id="element-614" data-mp="2pz5oyg1qy276">
                        <h4 className="font-medium text-gray-900" data-id="element-615" data-mp="2q0gscera57xr">
                          {dept.name}
                        </h4>
                        <a href={`mailto:${dept.email}`} className="text-blue-600 hover:text-blue-800" data-id="element-616" data-mp="2lzn2hfua1j28">
                          {dept.email}
                        </a>
                      </div>
                    </div>)}
                </div>
              </div>
              <div data-id="element-617" data-mp="34xhl8ll7j8xt">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-618" data-mp="34vwnyo7idbw9">
                  Follow Us
                </h3>
                <div className="flex space-x-4" data-id="element-619" data-mp="3iq780s8k8hvz">
                  <a href="#" className="bg-blue-100 p-2 rounded-full text-blue-600 hover:bg-blue-200" data-id="element-620" data-mp="3doq7wyxwgzgg">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-621" data-mp="3sjwje1frlqh6">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" data-id="element-622" data-mp="3us771iaaz58q" />
                    </svg>
                  </a>
                  <a href="#" className="bg-blue-100 p-2 rounded-full text-blue-600 hover:bg-blue-200" data-id="element-623" data-mp="m8rbo4f3kh9k">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-624" data-mp="w7p4vkngwyyw">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" data-id="element-625" data-mp="2xzvaou1pr256" />
                    </svg>
                  </a>
                  <a href="#" className="bg-blue-100 p-2 rounded-full text-blue-600 hover:bg-blue-200" data-id="element-626" data-mp="2bs7xamk218xz">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-627" data-mp="39ddmjfsb7smt">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-628" data-mp="2zfpghtp96ypc" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="lg:col-span-2" data-id="element-629" data-mp="2dr27rqdt88rt">
              {isSubmitted ? <div className="bg-white p-8 rounded-lg shadow-md" data-id="element-630" data-mp="2iq5d7bxpch0b">
                  <div className="text-center" data-id="element-631" data-mp="2mqxn87eeepxn">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-6" data-id="element-632" data-mp="2mpmju8ov7k72">
                      <CheckCircleIcon size={32} data-id="element-633" data-mp="3aep0bd7rj6zz" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-634" data-mp="3f8t79wh13f12">
                      Thank You!
                    </h3>
                    <p className="text-lg text-gray-700 mb-6" data-id="element-635" data-mp="yt6czt9fvz73">
                      Your message has been sent successfully. We'll get back to
                      you as soon as possible.
                    </p>
                    <button onClick={() => setIsSubmitted(false)} className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300" data-id="element-636" data-mp="18j9hapm0gzkq">
                      Send Another Message
                    </button>
                  </div>
                </div> : <div className="bg-white p-8 rounded-lg shadow-md" data-id="element-637" data-mp="psrpqocx0a0y">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-638" data-mp="pukxk6z9l0aj">
                    Send Us a Message
                  </h3>
                  <form onSubmit={handleSubmit} data-id="element-639" data-mp="217x2ymao9qyt">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-640" data-mp="2175ctqzuglfx">
                      <div data-id="element-641" data-mp="1xrl8hjb1nr5y">
                        <label htmlFor="name" className="block text-gray-700 font-medium mb-2" data-id="element-642" data-mp="1xuzagqfspycv">
                          Full Name *
                        </label>
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full px-4 py-3 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500`} placeholder="Your name" data-id="element-643" data-mp="1j4ibreujpmjo" />
                        {formErrors.name && <p className="mt-1 text-red-500 text-sm" data-id="element-644" data-mp="2zhqdd78et0cy">
                            {formErrors.name}
                          </p>}
                      </div>
                      <div data-id="element-645" data-mp="322535wb4j6gu">
                        <label htmlFor="email" className="block text-gray-700 font-medium mb-2" data-id="element-646" data-mp="320tzrxllc0q9">
                          Email Address *
                        </label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500`} placeholder="Your email" data-id="element-647" data-mp="3gqj3o0l2iiu8" />
                        {formErrors.email && <p className="mt-1 text-red-500 text-sm" data-id="element-648" data-mp="8e3r2yf1tomq">
                            {formErrors.email}
                          </p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-649" data-mp="24f5shu10abs1">
                      <div data-id="element-650" data-mp="24h9sw1zp4snk">
                        <label htmlFor="phone" className="block text-gray-700 font-medium mb-2" data-id="element-651" data-mp="2if6td9h9tj7i">
                          Phone Number (Optional)
                        </label>
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Your phone number" data-id="element-652" data-mp="1qlekiq2xazyf" />
                      </div>
                      <div data-id="element-653" data-mp="12xo8uip4bwar">
                        <label htmlFor="subject" className="block text-gray-700 font-medium mb-2" data-id="element-654" data-mp="stzqx6spkp0e">
                          Subject
                        </label>
                        <select id="subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-655" data-mp="iwv372j7slhh">
                          <option value="General Inquiry" data-id="element-656" data-mp="11mjrwdwh35oe">
                            General Inquiry
                          </option>
                          <option value="Research Collaboration" data-id="element-657" data-mp="16k35dblcjefa">
                            Research Collaboration
                          </option>
                          <option value="Certification Program" data-id="element-658" data-mp="3rbltlbo1ryjo">
                            Certification Program
                          </option>
                          <option value="Partnership Opportunity" data-id="element-659" data-mp="be5hwfno1nl8">
                            Partnership Opportunity
                          </option>
                          <option value="Volunteer Interest" data-id="element-660" data-mp="1naxlgtzcy2f">
                            Volunteer Interest
                          </option>
                          <option value="Media Inquiry" data-id="element-661" data-mp="3sv6qgutx73nx">Media Inquiry</option>
                        </select>
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-662" data-mp="pclqyd903q6j">
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2" data-id="element-663" data-mp="pdofieiupgja">
                        Message *
                      </label>
                      <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={5} className={`w-full px-4 py-3 border ${formErrors.message ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500`} placeholder="How can we help you?" data-id="element-664" data-mp="fhbqvv25xsve"></textarea>
                      {formErrors.message && <p className="mt-1 text-red-500 text-sm" data-id="element-665" data-mp="1uctpth414m7i">
                          {formErrors.message}
                        </p>}
                    </div>
                    <button type="submit" disabled={isSubmitting} className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200 flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-666" data-mp="23boz522decf1">
                      {isSubmitting ? <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-667" data-mp="iyzf7fnze6z6">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-668" data-mp="ksa6rqtg9ikz"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-669" data-mp="13le3xm5vmdkc"></path>
                          </svg>
                          Sending...
                        </> : <>
                          <SendIcon size={18} className="mr-2" data-id="element-670" data-mp="1s3wo5uo03qjl" />
                          Send Message
                        </>}
                    </button>
                  </form>
                </div>}
            </motion.div>
          </div>
        </div>
      </section>
      {/* Map Section */}
      <section className="py-16 bg-white" data-id="element-671" data-mp="18h8egbvhzd1l">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-672" data-mp="18ggtrw3fuzq3">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-673" data-mp="1d8qjpc4jzzsd">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-674" data-mp="uiifsrt155b4">
              Our Location
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-675" data-mp="1a0a3j75fpa5j">
              Visit our research facilities in Cambridge, MA to learn more about
              our work and explore partnership opportunities
            </p>
          </motion.div>
          <div className="rounded-lg overflow-hidden shadow-md" data-id="element-676" data-mp="111evdpitxpzl">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2948.1887066967843!2d-71.09353522346934!3d42.36229483289426!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e370a5b5f51fbb%3A0xd7a2b3bcd4ac5c8!2sMassachusetts%20Institute%20of%20Technology!5e0!3m2!1sen!2sus!4v1690389000000!5m2!1sen!2sus" width="100%" height="500" style={{
            border: 0
          }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Ukweli Institute Location" data-id="element-677" data-mp="vzfq2a3j4j00"></iframe>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-gray-50" data-id="element-678" data-mp="3pcrarji94u60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-679" data-mp="3ubu8eivwjy3y">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-680" data-mp="3ue6v19cluwnc">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-681" data-mp="3blvyrycghwy7">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-682" data-mp="3giups80tpns6">
              Find answers to common questions about contacting and working with
              the Ukweli Institute
            </p>
          </motion.div>
          <div className="space-y-6" data-id="element-683" data-mp="2sula7nu7dw9h">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-684" data-mp="2stjydh5d55qp">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-685" data-mp="683pr88fdl0e">
                How quickly can I expect a response to my inquiry?
              </h3>
              <p className="text-gray-700" data-id="element-686" data-mp="b6wxek116hsn">
                We strive to respond to all inquiries within 1-2 business days.
                For urgent matters, please specify in your message subject line.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-687" data-mp="3jn03ilchvyev">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-688" data-mp="375pao62nhydt">
                How can I schedule a tour of your research facilities?
              </h3>
              <p className="text-gray-700" data-id="element-689" data-mp="2x6gjtm330zvo">
                Facility tours are available by appointment for potential
                research partners, donors, and educational groups. Please
                contact us through the form above and select "General Inquiry"
                to request a tour.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-690" data-mp="2jcgxsmx54cpa">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-691" data-mp="1pvvffs93xohf">
                How can my company participate in the certification program?
              </h3>
              <p className="text-gray-700" data-id="element-692" data-mp="2iig9j0gyweoo">
                To learn more about our certification process, please select
                "Certification Program" in the subject dropdown when contacting
                us. Our certification team will provide details on the
                application process, requirements, and timeline.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-693" data-mp="31hu1xdz1thd2">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-694" data-mp="14ilf3rfw572k">
                Do you offer internship or research opportunities?
              </h3>
              <p className="text-gray-700" data-id="element-695" data-mp="bu7h3bosyar0">
                Yes, we offer internships and research positions for qualified
                candidates. Please visit our Careers page for current openings
                or contact us with "Volunteer Interest" in the subject line to
                inquire about upcoming opportunities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
};